/**
 * Хук для загрузки счетов авторизованного пользователя
 * 
 * Загружает список лицевых счетов авторизованного пользователя и обогащает их информацией о тарифах.
 * Предоставляет удобный интерфейс для работы со счетами.
 */

import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/client';

/**
 * Информация о лицевом счете
 */
export interface SubscriberInfo {
  id: string;
  accountNumber: string;
  phone?: string;
  address: string;
  balance: number;
  isBlocked: boolean;
  paidUntil?: string | null;
  lastBillingDate?: string | null;
  tariff?: {
    id: string;
    name: string;
    monthlyFee: number;
  } | null;
}

/**
 * Расширенная информация о лицевом счете с данными о тарифе
 */
export interface AccountInfo extends SubscriberInfo {
  /** Стоимость тарифа */
  tariffCost: number;
}

/**
 * Результат хука useAuthenticatedAccounts
 */
export interface UseAuthenticatedAccountsResult {
  /** Список лицевых счетов с информацией о тарифах */
  accounts: AccountInfo[];
  /** Флаг загрузки данных */
  isLoading: boolean;
  /** Ошибка при загрузке */
  error: Error | null;
  /** Флаг успешной загрузки */
  isSuccess: boolean;
  /** Функция для повторной загрузки данных */
  refetch: () => void;
  /** Флаг наличия нескольких счетов */
  hasMultipleAccounts: boolean;
  /** Первый счет (для случая с одним счетом) */
  primaryAccount: AccountInfo | null;
}

/**
 * Получение стоимости тарифа из данных абонента
 * 
 * @param subscriber - Информация о лицевом счете
 * @returns Стоимость тарифа
 */
const calculateTariffCost = (subscriber: SubscriberInfo): number => {
  return subscriber.tariff?.monthlyFee || 0;
};

/**
 * Преобразование SubscriberInfo в AccountInfo с добавлением информации о тарифе
 * 
 * @param subscriber - Информация о лицевом счете
 * @returns Расширенная информация о счете
 */
const enrichAccountInfo = (subscriber: SubscriberInfo): AccountInfo => {
  return {
    ...subscriber,
    tariffCost: calculateTariffCost(subscriber),
  };
};

/**
 * Проверка авторизации пользователя
 * 
 * @returns true, если пользователь авторизован
 */
const isAuthenticated = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  
  const token = localStorage.getItem('app-web-auth-token');
  return !!token;
};

/**
 * Хук для загрузки счетов авторизованного пользователя
 * 
 * Загружает список лицевых счетов пользователя и обогащает их информацией о тарифах.
 * Предоставляет удобный интерфейс для работы со счетами, включая флаги состояния
 * и вспомогательные свойства.
 * 
 * @returns Объект с данными счетов и функциями для работы с ними
 * 
 * @example
 * ```tsx
 * const { accounts, isLoading, hasMultipleAccounts, primaryAccount, error } = useAuthenticatedAccounts();
 * 
 * if (isLoading) {
 *   return <Skeleton />;
 * }
 * 
 * if (error) {
 *   return <ErrorMessage error={error} />;
 * }
 * 
 * if (hasMultipleAccounts) {
 *   // Показать селектор счетов
 *   return <AccountSelector accounts={accounts} />;
 * }
 * 
 * // Работать с единственным счетом
 * return <AccountCard account={primaryAccount} />;
 * ```
 */
export const useAuthenticatedAccounts = (): UseAuthenticatedAccountsResult => {
  const [accounts, setAccounts] = useState<AccountInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  /**
   * Загрузка счетов с сервера
   */
  const fetchAccounts = useCallback(async () => {
    // Проверяем авторизацию
    if (!isAuthenticated()) {
      setAccounts([]);
      setIsLoading(false);
      setError(null);
      setIsSuccess(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setIsSuccess(false);

    try {
      // Получаем токен из localStorage
      const token = localStorage.getItem('app-web-auth-token');
      
      // Запрос к API для получения счетов
      const response = await apiClient.get<{ status: string; data: SubscriberInfo[] }>('/customer/subscribers', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Проверка корректности ответа
      if (!response || !response.data) {
        throw new Error('Некорректный ответ от сервера');
      }

      // Извлекаем данные из обёртки { status, data }
      const subscribersData = response.data.data;

      // Проверка на пустой массив
      if (!Array.isArray(subscribersData)) {
        console.error('Ответ не является массивом:', subscribersData);
        console.error('Тип ответа:', typeof subscribersData);
        if (subscribersData && typeof subscribersData === 'object') {
          console.error('Ключи объекта:', Object.keys(subscribersData));
        }
        throw new Error('Некорректный формат данных');
      }

      // Валидация данных счетов
      const validAccounts = subscribersData.filter((account) => {
        if (!account || !account.id || !account.accountNumber) {
          console.warn('Пропущен некорректный счет:', account);
          return false;
        }
        return true;
      });

      if (validAccounts.length === 0 && subscribersData.length > 0) {
        console.warn('Все счета имеют некорректный формат');
      }

      // Обогащаем данные счетов информацией о тарифах
      const enrichedAccounts = validAccounts.map(enrichAccountInfo);

      setAccounts(enrichedAccounts);
      setIsSuccess(true);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ошибка загрузки счетов';
      console.error('Ошибка загрузки счетов:', err);
      setError(new Error(errorMessage));
      setAccounts([]);
      setIsSuccess(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Загрузка счетов при монтировании компонента
   */
  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Вычисляем вспомогательные свойства
  const hasMultipleAccounts = accounts.length > 1;
  const primaryAccount = accounts.length > 0 ? accounts[0] : null;

  // Логируем состояние для отладки (только реальные ошибки сети/API)
  if (error && isAuthenticated()) {
    console.error('Ошибка загрузки счетов:', error);
  }

  if (isSuccess && accounts.length === 0) {
    console.warn('Счета успешно загружены, но список пуст');
  }

  return {
    accounts,
    isLoading,
    error,
    isSuccess,
    refetch: fetchAccounts,
    hasMultipleAccounts,
    primaryAccount,
  };
};
